import { useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { cn } from '@/shared/lib/utils'

export interface BottomNavItem {
  /**
   * Идентификатор пункта меню
   */
  id: string
  /**
   * Иконка
   */
  icon: React.ComponentType<{ className?: string }>
  /**
   * Текст пункта меню
   */
  label: string
  /**
   * Активен ли пункт меню
   */
  active?: boolean
  /**
   * Обработчик клика
   */
  onClick: () => void
  /**
   * Показывать ли бейдж с количеством
   */
  showBadge?: boolean
  /**
   * Количество для бейджа
   */
  badgeCount?: number
}

export interface BottomNavProps {
  /**
   * Пункты меню
   */
  items: BottomNavItem[]
  /**
   * Дополнительные классы
   */
  className?: string
}

/**
 * UI компонент: Нижняя навигация
 * 
 * Чистый UI компонент для отображения фиксированной нижней панели навигации
 * с поддержкой safe area insets и адаптацией под Telegram Mini App.
 * Не содержит бизнес-логики - все данные передаются через props.
 * 
 * @example
 * ```tsx
 * <BottomNav
 *   items={[
 *     {
 *       id: 'home',
 *       icon: Home,
 *       label: 'Главная',
 *       active: true,
 *       onClick: () => navigate('/'),
 *     },
 *   ]}
 * />
 * ```
 */
export function BottomNav({
  items,
  className,
}: BottomNavProps) {
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)')
    const syncBottomNavHeight = () => {
      document.documentElement.style.setProperty(
        '--bottom-nav-height',
        mediaQuery.matches ? '0px' : 'calc(64px + env(safe-area-inset-bottom, 0px))',
      )
    }

    syncBottomNavHeight()
    mediaQuery.addEventListener('change', syncBottomNavHeight)

    return () => {
      mediaQuery.removeEventListener('change', syncBottomNavHeight)
      document.documentElement.style.setProperty('--bottom-nav-height', '0px')
    }
  }, [])

  return (
    <motion.nav
      initial={{ y: 0 }}
      className={cn(
        'fixed bottom-0 left-0 right-0 z-40',
        // Light theme
        'bg-white border-t border-silk',
        // Dark theme - средний уровень поверхности (#141618)
        'dark:bg-dark-elevated dark:border-t dark:border-dark-border',
        'shadow-lg',
        'safe-bottom',
        // Скрываем на планшете и выше (там используется SideNav)
        'md:hidden',
        className
      )}
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        height: 'calc(64px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <div className="flex items-center justify-around h-16 px-2">
        {items.map((item) => {
          const Icon = item.icon
          const badgeCount = item.showBadge ? item.badgeCount || 0 : 0

          return (
            <button
              key={item.id}
              type="button"
              onClick={item.onClick}
              className={cn(
                'flex flex-col items-center justify-center',
                'flex-1 h-full',
                'transition-colors duration-150',
                'touch-target',
                'relative',
                {
                  // Light theme
                  'text-primary': item.active,
                  'text-gray-500': !item.active,
                  // Dark theme - активная вкладка ярче (+30% яркости)
                  'dark:text-primary-light': item.active,
                  'dark:text-gray-400': !item.active,
                }
              )}
              aria-label={item.label}
              aria-current={item.active ? 'page' : undefined}
            >
              <motion.div
                className="relative"
                initial={false}
                animate={{
                  scale: item.active ? 1 : 1,
                }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 17,
                }}
              >
                <motion.div
                  animate={{
                    scale: item.active ? [1, 1.15, 1] : 1,
                  }}
                  transition={{
                    duration: 0.3,
                    ease: "easeOut",
                  }}
                >
                  <Icon
                    className={cn('h-6 w-6', {
                      'stroke-[2.5]': item.active,
                      'stroke-[1.5]': !item.active,
                    })}
                  />
                </motion.div>
                <AnimatePresence>
                  {badgeCount > 0 && (
                    <motion.span
                      key={`badge-${badgeCount}`}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ 
                        scale: [1, 1.3, 1],
                        opacity: 1,
                      }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ 
                        duration: 0.3,
                        ease: 'easeOut',
                      }}
                      className={cn(
                        'absolute -top-2 -right-2',
                        'min-w-[18px] h-[18px] px-1',
                        'flex items-center justify-center',
                        'bg-primary text-white text-xs font-bold',
                        'rounded-full',
                        'border-2 border-white dark:border-dark-elevated'
                      )}
                      aria-label={`${badgeCount} товаров в корзине`}
                    >
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.div>
              <span
                className={cn(
                  'text-xs mt-1',
                  'font-medium',
                  {
                    // Light theme
                    'text-primary': item.active,
                    'text-gray-500': !item.active,
                    // Dark theme - активная вкладка ярче
                    'dark:text-primary-light': item.active,
                    'dark:text-gray-400': !item.active,
                  }
                )}
              >
                {item.label}
              </span>
              {item.active && (
                <motion.div
                  layoutId="bottom-nav-indicator"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                  initial={false}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              )}
            </button>
          )
        })}
      </div>
    </motion.nav>
  )
}
